import * as LucideIcons from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { fetchHomeSection, HomeSection } from "@/services/homeService";

import Smile from "../../assets/images/smile.jpg";

export default function ScooterSection() {
  const navigate = useNavigate();
  const [section, setSection] = useState<HomeSection<any> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHomeSection("explore").then((data) => {
      setSection(data);
      setLoading(false);
    });
  }, []);

  // Modern shimmer for loading
  function ModernShimmer({
    className = "",
    height,
    width,
  }: {
    className?: string;
    height: string;
    width: string;
  }) {
    return (
      <div
        className={`relative overflow-hidden rounded-xl shadow-sm ${className}`}
        style={{ height, width }}
      >
        <div
          className="animate-shimmer absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-base-200 dark:via-base-100 dark:to-base-200"
          style={{ backgroundSize: "200% 100%" }}
        />
      </div>
    );
  }

  if (
    typeof document !== "undefined" &&
    !document.getElementById("modern-shimmer-style")
  ) {
    const style = document.createElement("style");
    style.id = "modern-shimmer-style";
    style.innerHTML = `
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .animate-shimmer {
        animation: shimmer 1.5s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }

  // Helper to render Lucide icon by name
  function renderIcon(name: string) {
    const Icon = (LucideIcons as any)[name];
    return Icon ? <Icon className="text-primary" size={32} /> : null;
  }

  // Button click handler
  function handleButtonClick(e: React.MouseEvent, link: string) {
    e.preventDefault();
    if (link.startsWith("/")) {
      navigate(link);
    } else {
      window.open(link, "_blank");
    }
  }

  return (
    <section className="relative mx-auto my-8 flex min-h-[60vh] w-full max-w-7xl items-center justify-center overflow-hidden rounded-3xl bg-base-100 shadow-lg dark:bg-base-300">
      {/* Scooter as background */}
      <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-end">
        {loading ? (
          <ModernShimmer
            className="h-full max-w-[60%]"
            height="100%"
            width="60%"
          />
        ) : (
          <img
            alt="Scooter"
            className="h-full w-auto max-w-[60%] object-contain opacity-20 transition-all duration-500 md:opacity-30"
            loading="lazy"
            src={
              section?.content?.image
                ? section.content.image.startsWith("http")
                  ? section.content.image
                  : `${import.meta.env.VITE_API_STORAGE_URL}${section.content.image}`
                : Smile
            }
            style={{ bottom: 0, right: 0 }}
          />
        )}
      </div>
      {/* Overlay for readability */}
      <div className="absolute inset-0 z-10 bg-base-100/80 dark:bg-base-300/80" />

      {/* Content */}
      <div className="relative z-20 flex min-h-[60vh] w-full flex-col items-center justify-center px-4 py-12">
        {loading ? (
          <>
            <ModernShimmer className="mx-auto mb-4" height="3rem" width="60%" />
            <ModernShimmer className="mx-auto mb-6" height="2rem" width="80%" />
            <ModernShimmer
              className="mx-auto mb-3"
              height="2.5rem"
              width="90%"
            />
            <ModernShimmer
              className="mx-auto mb-3"
              height="2.5rem"
              width="70%"
            />
            <ModernShimmer
              className="mx-auto mb-3"
              height="2.5rem"
              width="80%"
            />
            <ModernShimmer
              className="mx-auto mb-3"
              height="2.5rem"
              width="60%"
            />
            <ModernShimmer className="mx-auto mb-3" height="3rem" width="40%" />
          </>
        ) : section && section.content ? (
          <>
            <h2 className="mb-4 text-center text-3xl font-extrabold text-primary md:text-5xl">
              {section.content.title}
            </h2>
            <p className="mb-6 max-w-lg text-center text-lg text-base-content/70">
              {section.content.subtitle}
            </p>
            <ul className="mb-6 w-full max-w-md space-y-3">
              {section.content.bullets?.map((b: any, i: number) => (
                <li
                  className="flex items-center justify-center gap-3 transition-transform hover:scale-105"
                  key={i}
                >
                  {renderIcon(b.icon)}
                  <span className="font-semibold">{b.text}</span>
                </li>
              ))}
            </ul>
            {section.content.button && (
              <a
                className="btn btn-primary btn-lg font-semibold transition-transform hover:scale-105"
                href={section.content.button.link}
                onClick={(e) =>
                  handleButtonClick(e, section.content.button.link)
                }
              >
                {section.content.button.label}
              </a>
            )}
          </>
        ) : null}
      </div>
    </section>
  );
}
